import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronLeft, FileCode } from 'lucide-react';
import SurveyItemGrid from './SurveyItemGrid';
import SurveyItemDetailView from './SurveyItemDetailView';
import useDocumentManager from '../../hooks/useDocumentManager';
import { parseSurveyItems, formatSurveyItemsForExport } from './surveyHelpers';
import DocumentToolbar from '../document/DocumentToolbar';

/**
 * Main Survey Editor component that integrates with DocumentEditor shell
 */
const SurveyEditor = () => {
  const { activeDocument, updateDocument, saveDocument } = useDocumentManager();
  const [surveyItems, setSurveyItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [showHidden, setShowHidden] = useState(true);
  const [isDetailPaneExpanded, setIsDetailPaneExpanded] = useState(true);
  const [isRawMode, setIsRawMode] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [cardSize, setCardSize] = useState('medium');
  
  const toggleDetailPane = () => {
    setIsDetailPaneExpanded(!isDetailPaneExpanded);
  };
  
  // Function to handle reordering of items
  const reorderItems = (fromIndex, toIndex) => {
    // Make sure indices are valid
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || 
        fromIndex >= surveyItems.length || toIndex > surveyItems.length) {
      return;
    }
    
    // Create a new array with the items in the new order
    const newItems = [...surveyItems];
    const [movedItem] = newItems.splice(fromIndex, 1);
    newItems.splice(toIndex, 0, movedItem);
    
    // Update state and document
    setSurveyItems(newItems);
    
    if (activeDocument) {
      updateDocument(activeDocument.id, {
        content: formatSurveyItemsForExport(newItems),
        isDirty: true
      });
    }
  };
  
  // Parse survey items from document content when document changes
  useEffect(() => {
    if (activeDocument?.content) {
      try {
        // Parse the document content into survey items
        const items = parseSurveyItems(activeDocument.content);
        setSurveyItems(items);
        
        // Select the first item if available
        if (items.length > 0 && !selectedItemId) {
          setSelectedItemId(items[0].refId);
        }
      } catch (error) {
        console.error('Error parsing survey items:', error);
      }
    }
  }, [activeDocument, selectedItemId]);
  
  // The selectedItem is calculated but not used in this component.
  // It may be needed in the future, so we'll comment it out for now.
  // const selectedItem = surveyItems.find(item => item.refId === selectedItemId) || null;
  
  // Filter items based on visibility setting
  const filteredItems = showHidden 
    ? surveyItems 
    : surveyItems.filter(item => item.visible);
  
  // Update an item in the survey
  const updateItem = (updatedItem) => {
    const newItems = surveyItems.map(item => 
      item.refId === updatedItem.refId ? updatedItem : item
    );
    
    setSurveyItems(newItems);
    
    // Update the document content with the updated items
    if (activeDocument) {
      // Format items back to the proper text format
      updateDocument(activeDocument.id, {
        content: formatSurveyItemsForExport(newItems),
        isDirty: true
      });
    }
  };
  
  // When detail pane is collapsed, we want to handle selected item differently
  useEffect(() => {
    if (!isDetailPaneExpanded && selectedItemId) {
      // Keep the selection in the grid for visual indication
      // But when we expand the detail pane again, we should still have the item selected
    }
  }, [isDetailPaneExpanded, selectedItemId]);
  
  // Toggle item visibility
  const toggleItemVisibility = (itemId) => {
    const newItems = surveyItems.map(item => 
      item.refId === itemId 
        ? { ...item, visible: !item.visible } 
        : item
    );
    
    setSurveyItems(newItems);
    
    // Update the document content
    if (activeDocument) {
      updateDocument(activeDocument.id, {
        content: formatSurveyItemsForExport(newItems),
        isDirty: true
      });
    }
  };
  
  // Context values are being used directly via props instead of context
  
  if (!activeDocument) {
    return (
      <div className="flex h-full items-center justify-center text-gray-500">
        <div className="text-center p-8">
          <p className="text-xl mb-4">No survey selected</p>
          <p>Open a survey from the sidebar or create a new one.</p>
        </div>
      </div>
    );
  }
  
  // Handle title change
  const handleTitleChange = (newTitle) => {
    if (activeDocument) {
      updateDocument(activeDocument.id, { 
        title: newTitle, 
        isDirty: true 
      });
    }
  };
  
  // Handle save
  const handleSave = () => {
    if (activeDocument) {
      saveDocument(activeDocument.id);
    }
  };
  
  // Handle export to Oscar (placeholder)
  const handleExportToOscar = () => {
    console.log('Export to Oscar - placeholder');
  };
  
  // Handle export to Word (placeholder)
  const handleExportToWord = () => {
    console.log('Export to Word - placeholder');
  };
  
  // Toggle raw mode
  const toggleRawMode = () => {
    setIsRawMode(!isRawMode);
  };
  
  // Handle content change in raw mode
  const handleRawContentChange = (e) => {
    const newContent = e.target.value;
    
    if (activeDocument) {
      updateDocument(activeDocument.id, {
        content: newContent,
        isDirty: true
      });
    }
  };
  
  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <DocumentToolbar 
        document={activeDocument}
        onSave={handleSave}
        onTitleChange={handleTitleChange}
        onExportToOscar={handleExportToOscar}
        onExportToWord={handleExportToWord}
        canUndo={false}
        canRedo={false}
      />
      
      {/* Raw mode toggle */}
      <div className="flex justify-end items-center p-1 bg-gray-100 border-b border-gray-300">
        <button 
          className={`flex items-center px-2 py-1 rounded text-xs ${isRawMode ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
          onClick={toggleRawMode}
        >
          <FileCode size={12} className="mr-1" />
          {isRawMode ? 'Survey View' : 'Raw Mode'}
        </button>
      </div>
      
      {/* Main content area */}
      {isRawMode ? (
        <div className="flex-1 overflow-hidden p-2 bg-gray-100">
          <textarea
            className="w-full h-full p-4 font-mono text-sm border rounded shadow-inner bg-white"
            value={activeDocument?.content || ''}
            onChange={handleRawContentChange}
            spellCheck="false"
          />
        </div>
      ) : (
        <div className="flex flex-1 h-full bg-gray-100 relative w-full overflow-hidden">
          {/* Left Sidebar - Master View */}
          <div className={`${isDetailPaneExpanded ? 'w-2/5' : 'w-full'} bg-white shadow-md overflow-auto transition-all duration-300 min-w-0`}>
            <SurveyItemGrid 
              items={filteredItems}
              selectedItemId={selectedItemId}
              setSelectedItemId={setSelectedItemId}
              toggleItemVisibility={toggleItemVisibility}
              showHidden={showHidden}
              setShowHidden={setShowHidden}
              maxColumns={isDetailPaneExpanded ? 5 : 12} // Allow more columns when detail pane is collapsed
              reorderItems={reorderItems}
              isDragging={isDragging}
              setIsDragging={setIsDragging}
              cardSize={cardSize}
              setCardSize={setCardSize}
            />
          </div>
          
          {/* Collapse/Expand button */}
          <button 
            className="absolute top-1/2 z-10 bg-gray-200 hover:bg-gray-300 p-0.5 rounded-full transform -translate-y-1/2"
            style={{ right: isDetailPaneExpanded ? 'calc(60% - 12px)' : '12px' }}
            onClick={toggleDetailPane}
            title={isDetailPaneExpanded ? "Collapse detail pane" : "Expand detail pane"}
          >
            {isDetailPaneExpanded ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
          </button>
          
          {/* Right Panel - Detail View */}
          {isDetailPaneExpanded && (
            <div className="flex-1 bg-gray-50 overflow-hidden transition-all duration-300">
              <SurveyItemDetailView 
                items={filteredItems}
                selectedItemId={selectedItemId}
                setSelectedItemId={setSelectedItemId}
                updateItem={updateItem}
              />
            </div>
          )}
      </div>
      )}
    </div>
  );
};

export default SurveyEditor;